import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { requiredFieldStats } from "../lib/intakeSchema";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import Header from "../components/app/Header";
import CreateListingDialog from "./CreateListingDialog";
import UpcomingWidget from "../components/app/UpcomingWidget";
import DashboardTimeline from "../components/app/DashboardTimeline";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { LISTING_STATUSES, STATUS_STYLES, computeProgress } from "../lib/phases";
import { Plus, MapPin, Calendar, Trash2, Search, ArrowUpRight, Mail, MessageSquare, LayoutGrid, GanttChart } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";

function StatusBadge({ status }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] font-medium tracking-wide ${STATUS_STYLES[status] || STATUS_STYLES["Pre-Listing"]}`}
    >
      {status}
    </span>
  );
}

function countUnread(listing) {
  const comments = listing.task_comments || [];
  const lastRead = listing.agent_last_read_comments_at;
  return comments.filter(
    (c) => c.author === "seller" && (!lastRead || c.created_at > lastRead)
  ).length;
}

function ClosingChip({ closingDate, listingId }) {
  const target = new Date(`${closingDate}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((target - today) / 86400000);
  let label;
  let tone;
  if (days > 7) {
    label = `Closes in ${days} days`;
    tone = "bg-emerald-50 border-emerald-200 text-emerald-800";
  } else if (days > 1) {
    label = `Closes in ${days} days`;
    tone = "bg-amber-50 border-amber-300 text-amber-900";
  } else if (days === 1) {
    label = "Closes tomorrow";
    tone = "bg-amber-100 border-amber-400 text-amber-900";
  } else if (days === 0) {
    label = "Closing today";
    tone = "bg-amber-100 border-amber-400 text-amber-900";
  } else {
    label = `Closed ${Math.abs(days)}d ago`;
    tone = "bg-stone-100 border-stone-300 text-stone-600";
  }
  return (
    <div
      data-testid={`listing-closing-chip-${listingId}`}
      className={`mt-2 inline-flex items-center gap-1 rounded-full border px-2 h-6 text-[10px] font-semibold uppercase tracking-widest ${tone}`}
    >
      <Calendar className="h-2.5 w-2.5" />
      <span>{label}</span>
      <span className="opacity-60">·</span>
      <span className="normal-case font-normal tracking-normal">
        {target.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
      </span>
    </div>
  );
}

const CONTINGENCY_IDS = ["t6_3_a", "t6_3_b", "t6_3_c", "t6_3_e", "t6_3_f", "t6_3_g", "t6_3_h", "t6_3_d"];

function ContingencyChip({ listing }) {
  const tasks = listing.tasks || {};
  const doneCount = CONTINGENCY_IDS.filter((id) => tasks[id]?.done).length;
  const status = listing.listing_status;
  const inEscrow = status === "Pending" || status === "Closed";
  // Only show once the listing has reached escrow or at least one contingency has cleared
  if (!inEscrow && doneCount === 0) return null;
  const all = doneCount === CONTINGENCY_IDS.length;
  return (
    <div
      data-testid={`listing-contingency-chip-${listing.id}`}
      className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-[11px] font-medium border ${
        all
          ? "bg-emerald-50 text-emerald-800 border-emerald-200"
          : "bg-stone-50 text-stone-700 border-stone-200"
      }`}
      title={`${doneCount} of ${CONTINGENCY_IDS.length} contingencies cleared`}
    >
      <span className="uppercase tracking-wider text-[9px] font-semibold opacity-70">
        Contingencies
      </span>
      <span className="tabular-nums font-heading">
        {doneCount} of {CONTINGENCY_IDS.length} cleared
      </span>
      <span className="flex items-center gap-[3px]">
        {CONTINGENCY_IDS.map((id) => (
          <span
            key={id}
            className={`h-1.5 w-1.5 rounded-full ${
              tasks[id]?.done ? "bg-emerald-600" : "bg-stone-300"
            }`}
          />
        ))}
      </span>
    </div>
  );
}

function ListingCard({ listing, index, onDelete }) {
  const { done, total, pct } = computeProgress(listing);
  const idx = String(index + 1).padStart(2, "0");
  const unread = countUnread(listing);
  return (
    <div
      data-testid={`listing-card-${listing.id}`}
      className="group relative flex flex-col rounded-md border border-stone-200 bg-white p-6 transition-transform duration-200 hover:-translate-y-0.5 hover:border-stone-300 hover:shadow-sm"
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <span className="section-index text-stone-500 text-base">Home {idx}</span>
        <div className="flex items-center gap-2">
          {unread > 0 && (
            <span
              data-testid={`unread-badge-${listing.id}`}
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white"
              style={{ backgroundColor: "#010082" }}
              title={`${unread} unread seller note${unread === 1 ? "" : "s"}`}
            >
              <MessageSquare className="h-2.5 w-2.5" />
              {unread} new
            </span>
          )}
          <StatusBadge status={listing.listing_status} />
          <button
            data-testid={`delete-listing-${listing.id}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete(listing);
            }}
            className="text-stone-400 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
            aria-label="Delete listing"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      <Link to={`/listing/${listing.id}`} className="flex-1 flex flex-col">
        <h3 className="text-xl font-heading font-semibold tracking-tight text-stone-900 leading-snug line-clamp-2">
          {listing.property_address || "Untitled property"}
        </h3>
        <p className="text-sm text-stone-500 mt-1.5 line-clamp-1">
          {listing.seller_names || "No seller listed"}
        </p>

        <div className="mt-6 space-y-3">
          <div className="flex items-center justify-between text-xs">
            <span className="text-stone-500">Workflow progress</span>
            <span className="font-medium text-stone-900 tabular-nums font-heading">
              {done} / {total}
            </span>
          </div>
          <Progress value={pct} className="h-1 bg-stone-100" />

          <div className="flex items-center justify-between pt-3 text-xs text-stone-500 border-t border-stone-100">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {listing.target_list_date
                  ? new Date(listing.target_list_date).toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : "No target date"}
              </span>
            </div>
            {listing.mls_number && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-stone-400">
                MLS {listing.mls_number}
              </span>
            )}
          </div>
          {listing.closing_date && (
            <ClosingChip closingDate={listing.closing_date} listingId={listing.id} />
          )}
          <ContingencyChip listing={listing} />
          {listing.intake_status !== "complete" && (listing.intake && Object.keys(listing.intake).length > 0) && (() => {
            const { filled, total } = requiredFieldStats(listing.intake);
            return (
              <span
                className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest border-amber-200 bg-amber-50 text-amber-800 w-fit"
                data-testid={`intake-draft-chip-${listing.id}`}
              >
                Draft · In Progress — {filled}/{total}
              </span>
            );
          })()}
        </div>

        <div className="mt-5 flex items-center gap-1.5 text-sm font-medium" style={{ color: "#010082" }}>
          Open workflow
          <ArrowUpRight className="h-3.5 w-3.5" />
        </div>
      </Link>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  useDocumentTitle();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [openCreate, setOpenCreate] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [view, setView] = useState("list"); // "list" | "timeline"

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/listings");
      setListings(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totalUnread = useMemo(
    () => listings.reduce((sum, l) => sum + countUnread(l), 0),
    [listings]
  );

  const filtered = useMemo(() => {
    const rows = listings.filter((l) => {
      if (statusFilter !== "all" && l.listing_status !== statusFilter) return false;
      if (unreadOnly && countUnread(l) === 0) return false;
      if (q.trim()) {
        const s = q.toLowerCase();
        return (
          (l.property_address || "").toLowerCase().includes(s) ||
          (l.seller_names || "").toLowerCase().includes(s) ||
          (l.mls_number || "").toLowerCase().includes(s)
        );
      }
      return true;
    });
    // Sort: nearest upcoming closing date first; listings without a closing date fall to the end (kept in original order).
    const withDate = rows
      .map((l, i) => ({ l, i, close: l.closing_date ? new Date(`${l.closing_date}T00:00:00`).getTime() : null }))
      .filter((r) => r.close !== null)
      .sort((a, b) => a.close - b.close)
      .map((r) => r.l);
    const withoutDate = rows.filter((l) => !l.closing_date);
    return [...withDate, ...withoutDate];
  }, [listings, q, statusFilter, unreadOnly]);

  // When "All statuses" is selected, group into hierarchical sections.
  // Active first, then in-progress, then wrapped-up/dead at the bottom.
  const STATUS_SECTION_ORDER = [
    "Active",
    "Pending",
    "Coming Soon",
    "Pre-Listing",
    "Withdrawn",
    "Cancelled",
    "Closed",
  ];
  const grouped = useMemo(() => {
    if (statusFilter !== "all") return null;
    const buckets = new Map(STATUS_SECTION_ORDER.map((s) => [s, []]));
    for (const l of filtered) {
      const key = buckets.has(l.listing_status) ? l.listing_status : "Active";
      buckets.get(key).push(l);
    }
    return STATUS_SECTION_ORDER
      .map((status) => ({ status, items: buckets.get(status) || [] }))
      .filter((g) => g.items.length > 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, statusFilter]);

  const handleCreated = (listing) => {
    setListings((prev) => [listing, ...prev]);
    setOpenCreate(false);
    toast.success("Home added");
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    const id = toDelete.id;
    try {
      await api.delete(`/listings/${id}`);
      setListings((prev) => prev.filter((x) => x.id !== id));
      toast.success("Home removed");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <Header />
      <main className="mx-auto max-w-7xl px-6 py-12 lg:py-16">
        {/* Editorial hero */}
        <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="overline text-stone-500 mb-4">Dashboard</p>
            <h1 className="text-4xl sm:text-5xl font-heading font-medium tracking-tight text-stone-900 leading-[1.05]">
              Every home we're guiding to <span className="font-serif-italic text-brand">the closing table.</span>
            </h1>
            <p className="text-sm text-stone-500 mt-4 max-w-lg">
              {listings.length} {listings.length === 1 ? "home" : "homes"} in workflow.
              Track deadlines across seven phases without a single sticky note.
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="send-digest-button"
              onClick={async () => {
                try {
                  const { data } = await api.post("/reminders/send-digest");
                  toast.success(`Digest sent · ${data.overdue} overdue, ${data.upcoming} upcoming`);
                } catch (e) {
                  toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send");
                }
              }}
              variant="outline"
              className="h-11 rounded-full px-4 border-stone-300"
            >
              <Mail className="h-4 w-4 mr-1.5" />
              Email me a digest
            </Button>
            <Button
              data-testid="new-listing-button"
              onClick={() => nav("/listings/new")}
              className="h-11 text-white rounded-full px-5"
              style={{ backgroundColor: "#010082" }}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add new home
            </Button>
          </div>
        </div>

        <div className="hr-serif mb-10" />

        {/* View switcher */}
        <div className="mb-6 flex items-center gap-2" data-testid="dashboard-view-switcher">
          <button
            type="button"
            onClick={() => setView("list")}
            aria-pressed={view === "list"}
            data-testid="dashboard-view-list"
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-widest border transition-colors ${
              view === "list"
                ? "text-white border-transparent"
                : "bg-white text-stone-600 border-stone-300 hover:border-stone-400"
            }`}
            style={view === "list" ? { backgroundColor: "#010082" } : undefined}
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            List view
          </button>
          <button
            type="button"
            onClick={() => setView("timeline")}
            aria-pressed={view === "timeline"}
            data-testid="dashboard-view-timeline"
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-xs font-semibold uppercase tracking-widest border transition-colors ${
              view === "timeline"
                ? "text-white border-transparent"
                : "bg-white text-stone-600 border-stone-300 hover:border-stone-400"
            }`}
            style={view === "timeline" ? { backgroundColor: "#010082" } : undefined}
          >
            <GanttChart className="h-3.5 w-3.5" />
            Timeline
          </button>
        </div>

        {view === "timeline" ? (
          <div className="grid grid-cols-1 gap-10">
            {loading ? (
              <div className="h-96 rounded-md border border-stone-200 bg-white animate-pulse" />
            ) : (
              <DashboardTimeline listings={listings} />
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <section className="lg:col-span-8">
            <div className="mb-6 flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
                <Input
                  data-testid="listings-search-input"
                  placeholder="Search by address, seller, MLS..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-10 h-11 rounded-full bg-white border-stone-300"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter-trigger" className="w-full sm:w-56 h-11 rounded-full bg-white border-stone-300">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {LISTING_STATUSES.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                data-testid="unread-only-toggle"
                onClick={() => setUnreadOnly((v) => !v)}
                aria-pressed={unreadOnly}
                className={`inline-flex items-center gap-1.5 h-11 px-4 rounded-full text-sm font-medium transition-colors whitespace-nowrap border ${
                  unreadOnly
                    ? "text-white border-transparent"
                    : "bg-white text-stone-700 border-stone-300 hover:border-stone-400"
                }`}
                style={unreadOnly ? { backgroundColor: "#010082" } : undefined}
              >
                <MessageSquare className="h-3.5 w-3.5" />
                Unread notes
                {totalUnread > 0 && (
                  <span
                    data-testid="unread-total-count"
                    className={`ml-0.5 inline-flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 text-[11px] font-semibold ${
                      unreadOnly ? "bg-white/25 text-white" : "text-white"
                    }`}
                    style={!unreadOnly ? { backgroundColor: "#010082" } : undefined}
                  >
                    {totalUnread}
                  </span>
                )}
              </button>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-56 rounded-md border border-stone-200 bg-white animate-pulse" />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div
                data-testid="empty-state"
                className="rounded-md border border-dashed border-stone-300 bg-white p-14 text-center"
              >
                <p className="overline text-stone-500 mb-4">No homes yet</p>
                <h3 className="text-2xl font-heading font-semibold tracking-tight text-stone-900">
                  Start with your first home.
                </h3>
                <p className="mt-3 text-sm text-stone-500 max-w-md mx-auto">
                  Add a home to unlock the 7-phase workflow with per-task deadlines.
                </p>
                <Button
                  data-testid="empty-state-create-button"
                  onClick={() => setOpenCreate(true)}
                  className="mt-7 text-white rounded-full px-5"
                  style={{ backgroundColor: "#010082" }}
                >
                  <Plus className="h-4 w-4 mr-1.5" />
                  Add new home
                </Button>
              </div>
            ) : (
              grouped ? (
                <div className="space-y-10" data-testid="listings-grouped">
                  {grouped.map((section) => (
                    <div key={section.status} data-testid={`listings-section-${section.status.toLowerCase().replace(/\s+/g, "-")}`}>
                      <div className="mb-4 flex items-baseline gap-3">
                        <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-600">
                          {section.status}
                        </h2>
                        <span className="text-[11px] tabular-nums text-stone-400 font-medium">
                          {section.items.length} {section.items.length === 1 ? "home" : "homes"}
                        </span>
                        <span className="flex-1 h-px bg-stone-200/70" />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {section.items.map((l, i) => (
                          <ListingCard key={l.id} listing={l} index={i} onDelete={setToDelete} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div
                  data-testid="listings-grid"
                  className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                >
                  {filtered.map((l, i) => (
                    <ListingCard key={l.id} listing={l} index={i} onDelete={setToDelete} />
                  ))}
                </div>
              )
            )}
          </section>

          <aside className="lg:col-span-4">
            <UpcomingWidget listings={listings} />
          </aside>
        </div>
        )}
      </main>

      <CreateListingDialog
        open={openCreate}
        onOpenChange={setOpenCreate}
        onCreated={handleCreated}
      />

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-listing-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading tracking-tight">Remove home?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove {toDelete?.property_address || "this home"} and all its task data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm"
              onClick={confirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
